import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';

export interface ActiveSession {
  id: string;
  device_info: any; // JSON from Supabase
  ip_address: string | null;
  user_agent: string;
  is_active: boolean;
  last_accessed_at: string;
  created_at: string;
  is_current?: boolean;
}

export function useActiveSessions() {
  const { user } = useUser();
  const toast = useTranslatedToast();
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadSessions();
    }
  }, [user]);

  const loadSessions = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('user_id', user.id as any)
        .eq('is_active', true as any)
        .order('last_accessed_at', { ascending: false });

      if (error) throw error;

      // Get current session token to mark current session
      const { data: { session } } = await supabase.auth.getSession();
      const currentSessionToken = session?.access_token;

      const sessionsWithCurrent = ((data as any[]) || []).map((s: any) => ({
        id: s.id,
        device_info: s.device_info,
        ip_address: s.ip_address as string | null,
        user_agent: s.user_agent,
        is_active: s.is_active,
        last_accessed_at: s.last_accessed_at,
        created_at: s.created_at,
        is_current: s.session_token === currentSessionToken
      }));

      setSessions(sessionsWithCurrent);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load active sessions');
    } finally {
      setLoading(false);
    }
  };

  const revokeSession = async (sessionId: string) => {
    if (!user?.id) return false;

    try {
      const { error } = await supabase
        .from('user_sessions')
        .update({ is_active: false } as any)
        .eq('id', sessionId as any)
        .eq('user_id', user.id as any);

      if (error) throw error;

      toast.success('Session revoked successfully');
      
      // Reload sessions to reflect changes
      await loadSessions();
      return true;
    } catch (error) {
      console.error('Error revoking session:', error);
      toast.error('Failed to revoke session');
      return false;
    }
  };

  const formatDeviceInfo = (session: ActiveSession): string => {
    const { device_info, user_agent } = session;
    
    // Try to use structured device_info if available
    if (device_info && typeof device_info === 'object') {
      const browser = device_info.browser;
      const os = device_info.os;
      if (browser && os) {
        return `${browser} on ${os}`;
      }
    }
    
    // Fallback to parsing user agent
    if (user_agent) {
      const browserMatch = user_agent.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/);
      const osMatch = user_agent.match(/(Windows|Mac|Linux|Android|iOS)/i);
      
      const browser = browserMatch ? browserMatch[1] : 'Unknown Browser';
      const os = osMatch ? osMatch[1] : 'Unknown OS';
      
      return `${browser} on ${os}`;
    }
    
    return 'Unknown Device';
  };

  const getLocationFromIP = (ip: string): string => {
    // In a real app, you'd use a geolocation service
    // For now, return a placeholder
    return 'Location unavailable';
  };

  return {
    sessions,
    loading,
    revokeSession,
    formatDeviceInfo,
    getLocationFromIP,
    refreshSessions: loadSessions
  };
}
